// Facade pubblico dell'SDK CloseYourIt, isomorfo browser/Node. Mantiene un
// singleton di [Client]; tutti i metodi sono no-op finché non si chiama [init]
// e nessuno solleva. Nell'IIFE (`globalName: CloseYourIt`) questi export
// diventano i metodi di `window.CloseYourIt`. Le integrazioni installate
// dipendono dalla piattaforma: window handlers/breadcrumb/http/jank nel
// browser, process handlers + lifecycle su Node.

import { Client } from './client'
import { installBreadcrumbs } from './integrations/browser/breadcrumbs'
import { installGlobalHandlers } from './integrations/browser/globalHandlers'
import { installHttpInstrumentation } from './integrations/browser/http'
import { installJankDetection } from './integrations/browser/jank'
import { installPageviews } from './integrations/browser/pageviews'
import { installNodeLifecycle } from './integrations/node/lifecycle'
import { installProcessHandlers } from './integrations/node/processHandlers'
import { CloseYourItLogger } from './logger'
import { isBrowser, isNode } from './runtime'
import type {
  CaptureHint,
  CloseYourItOptions,
  EventLevel,
  LogLevel,
  UserContext,
} from './types'
import type { StatKey } from './stats'

let currentClient: Client | null = null
let uninstallHandlers: (() => void) | null = null
let uninstallBreadcrumbs: (() => void) | null = null
let uninstallHttp: (() => void) | null = null
let uninstallJank: (() => void) | null = null
let uninstallPageviews: (() => void) | null = null
let uninstallLifecycle: (() => void) | null = null

/// Inizializza l'SDK. Idempotente: se è già attivo con config valida, non fa
/// nulla. Con config incompleta resta no-op (non solleva) e può essere superata
/// da una `init` successiva valida.
export function init(options: CloseYourItOptions): Client {
  if (currentClient && currentClient.enabled) return currentClient

  teardown()

  const client = new Client(options)
  currentClient = client

  if (client.enabled && client.options.autoInstall) {
    if (isBrowser()) {
      uninstallHandlers = installGlobalHandlers(client)
      if (client.options.breadcrumbs) {
        uninstallBreadcrumbs = installBreadcrumbs(client)
      }
      // Il patch http serve a due consumatori: breadcrumb http e/o verdetti
      // performance. Si installa se almeno uno è attivo.
      const wantsHttp =
        (client.options.breadcrumbs && client.options.autoBreadcrumbs.http) ||
        (client.options.detectPerformanceIssues && client.options.captureExternalHttp)
      if (wantsHttp) {
        uninstallHttp = installHttpInstrumentation(client)
      }
      if (client.options.detectPerformanceIssues && client.options.detectJank) {
        uninstallJank = installJankDetection(client)
      }
      if (client.options.trackPageviews) {
        uninstallPageviews = installPageviews(client)
      }
      uninstallLifecycle = installLifecycle(client)
    } else if (isNode()) {
      uninstallHandlers = installProcessHandlers(client)
      uninstallLifecycle = installNodeLifecycle(client)
    }
  }

  return client
}

/// Cattura un'eccezione o valore arbitrario.
export function captureException(error: unknown, hint?: CaptureHint): void {
  currentClient?.captureException(error, hint)
}

/// Cattura un messaggio diagnostico.
export function captureMessage(message: string, level: EventLevel = 'info'): void {
  currentClient?.captureMessage(message, level)
}

/// Bufferizza un log strutturato (inviato in batch).
export function log(
  level: LogLevel,
  message: string,
  attributes?: Record<string, unknown>,
  loggerName?: string,
): void {
  currentClient?.log(level, message, attributes, loggerName)
}

/// Logger applicativo con metodi per livello (`logger.warn('…')`), null-safe.
/// `logger.named('payments')` crea un logger figlio con nome.
export const logger = new CloseYourItLogger(() => currentClient)

/// Misura [body] e invia una metrica `slow_method` oltre soglia. Preserva la
/// sincronicità del body. Pre-init esegue comunque il body senza misurare.
/// Label vuota → TypeError (sempre, anche pre-init: errore del programmatore).
export function measure<T>(
  label: string,
  body: () => T,
  opts?: { args?: Record<string, unknown> },
): T {
  if (currentClient) return currentClient.measure(label, body, opts)
  if (typeof label !== 'string' || label.trim().length === 0) {
    throw new TypeError(
      'closeyourit: measure(label) richiede una label non vuota (è il fingerprint della metrica)',
    )
  }
  return body()
}

/// Cattura una pageview (web analytics). No-op fuori dal browser o pre-init.
/// [overrides.path] forza il path (default `location.pathname`). Con
/// `trackPageviews: true` l'auto-tracking la chiama al load e a ogni cambio di
/// pathname; usarla a mano per SPA con routing custom o eventi virtuali.
export function capturePageview(overrides?: { path?: string }): void {
  currentClient?.capturePageview(overrides)
}

/// Imposta l'utente corrente (`null` per rimuoverlo).
export function setUser(user: UserContext | null): void {
  currentClient?.setUser(user)
}

/// Imposta un tag.
export function setTag(key: string, value: string): void {
  currentClient?.setTag(key, value)
}

/// Imposta più tag in un colpo.
export function setTags(tags: Record<string, string>): void {
  currentClient?.setTags(tags)
}

/// Imposta un valore extra.
export function setExtra(key: string, value: unknown): void {
  currentClient?.setExtra(key, value)
}

/// Imposta un context nominato.
export function setContext(key: string, value: Record<string, unknown>): void {
  currentClient?.setContext(key, value)
}

/// Imposta l'ID di correlazione (default: generato per pagina).
export function setTraceId(id: string | undefined): void {
  currentClient?.setTraceId(id)
}

/// ID di correlazione corrente, o `undefined` se non inizializzato.
export function getTraceId(): string | undefined {
  return currentClient?.scope.traceId
}

/// Aggiunge un breadcrumb manuale.
export function addBreadcrumb(input: {
  message?: string
  category?: string
  type?: string
  level?: string
  data?: Record<string, unknown>
}): void {
  currentClient?.addBreadcrumb(input)
}

/// Azzera lo scope (user/tag/extra/context/breadcrumbs), tranne il trace_id.
export function clearScope(): void {
  currentClient?.clearScope()
}

/// Snapshot dei contatori diagnostici `{ enqueued, sent, dropped, failed }`.
export function getStats(): Record<StatKey, number> {
  return (
    currentClient?.stats.toObject() ?? {
      enqueued: 0,
      sent: 0,
      dropped: 0,
      failed: 0,
    }
  )
}

/// `true` se inizializzato e la configurazione consente l'invio.
export function isEnabled(): boolean {
  return currentClient?.enabled ?? false
}

/// Svuota i log bufferizzati e attende gli invii in volo.
export function flush(): Promise<void> {
  return currentClient?.flush() ?? Promise.resolve()
}

/// Spegne l'SDK, ripristina i patch globali e libera le risorse.
export async function close(): Promise<void> {
  const client = currentClient
  teardown()
  await client?.close()
  currentClient = null
}

function teardown(): void {
  uninstallHandlers?.()
  uninstallBreadcrumbs?.()
  uninstallHttp?.()
  uninstallJank?.()
  uninstallPageviews?.()
  uninstallLifecycle?.()
  uninstallHandlers = null
  uninstallBreadcrumbs = null
  uninstallHttp = null
  uninstallJank = null
  uninstallPageviews = null
  uninstallLifecycle = null
}

// Su `pagehide`/`visibilitychange(hidden)` svuota i log: l'invio parte con
// `fetch(keepalive)` e sopravvive alla chiusura della pagina.
function installLifecycle(client: Client): () => void {
  if (typeof window === 'undefined') return () => {}

  const onHide = (): void => {
    void client.flush()
  }
  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void client.flush()
    }
  }

  window.addEventListener('pagehide', onHide)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  return () => {
    window.removeEventListener('pagehide', onHide)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }
}

export { Client } from './client'
export { CloseYourItLogger } from './logger'
export { SDK_NAME, SDK_VERSION } from './version'
export type {
  AutoBreadcrumbsOptions,
  BeforeSend,
  CaptureHint,
  CloseYourItOptions,
  EventLevel,
  EventPayload,
  LogLevel,
  UserContext,
} from './types'
